import { CSSObject } from '@emotion/react';
import { FlexGridItemSize } from '../../theming/types/components/FlexGrid.types';
import {
    HoneycombTheme,
    ResponsiveStyleValue,
    SpacingValue,
    createResponsiveStyles,
    useHoneycombTheme,
    mergeResponsiveStyleValues,
} from '../../theming';

function appendLevel(level: number) {
    if (!level) {
        return '';
    }
    return `-level-${level}`;
}

const getItemStyles = (size: FlexGridItemSize, level: number, isNestedContainer: boolean): CSSObject => {
    if (size === 'auto') {
        return {
            flexGrow: 0,
            flexShrink: 0,
            maxWidth: 'none',
            flexBasis: 'auto',
            width: 'auto',
        };
    }
    if (size === 'fill') {
        return {
            flexGrow: 1,
            maxWidth: '100%',
            flexBasis: 0,
            width: 'auto',
        };
    }
    if (size === 'hold') {
        return {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: 'auto',
            width: 'auto',
        };
    }
    if (size === 'equal') {
        return {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            width: 'auto',
        };
    }

    const nestedWidthAdjustment = ` + var(--flex-grid-column-spacing${appendLevel(level)})`;
    const width = `calc(100% * ${size} / var(--flex-grid-columns${appendLevel(isNestedContainer ? level - 1 : level)})${
        isNestedContainer ? nestedWidthAdjustment : ''
    })`;

    return {
        flexGrow: 0,
        flexBasis: 'auto',
        width,
    };
};

function getContainerResponsiveStyles(theme: HoneycombTheme, level: number, columns: number) {
    return (values: { columnSpacing: SpacingValue; rowSpacing: SpacingValue }): CSSObject => {
        const columnSpacingValue = theme.fn.getSpacingValue(values.columnSpacing);
        const rowSpacingValue = theme.fn.getSpacingValue(values.rowSpacing);
        return {
            [`--flex-grid-columns${appendLevel(level)}`]: columns,
            [`--flex-grid-column-spacing${appendLevel(level)}`]: columnSpacingValue,
            [`--flex-grid-row-spacing${appendLevel(level)}`]: rowSpacingValue,
        };
    };
}

function getItemResponsiveStyles(level: number, isNestedContainer: boolean) {
    return (size: FlexGridItemSize): CSSObject => {
        return getItemStyles(size, level, isNestedContainer);
    };
}

function getItemPadding(axis: 'x' | 'y', level: number, isNestedContainer: boolean, disableEqualOverflow: boolean) {
    return `calc(var(--flex-grid-${axis === 'x' ? 'column' : 'row'}-spacing${appendLevel(
        isNestedContainer ? level - 1 : level
    )})${!disableEqualOverflow ? ' / 2' : ''})`;
}

function getContainerMargin(axis: 'x' | 'y', level: number, disableEqualOverflow: boolean) {
    return `calc(var(--flex-grid-${axis === 'x' ? 'column' : 'row'}-spacing${appendLevel(level)}) ${
        !disableEqualOverflow ? '/ -2' : '* -1'
    })`;
}

export interface FlexGridStylesParams {
    container: boolean;
    item: boolean;
    columns: number;
    columnSpacing?: ResponsiveStyleValue<SpacingValue>;
    rowSpacing?: ResponsiveStyleValue<SpacingValue>;
    xs?: FlexGridItemSize;
    s?: FlexGridItemSize;
    m?: FlexGridItemSize;
    l?: FlexGridItemSize;
    xl?: FlexGridItemSize;
    level: number;
    disableEqualOverflow: boolean;
}

type StyleClasses = 'root';

export function useStyles({
    columns,
    container,
    item,
    columnSpacing,
    rowSpacing,
    xs,
    s,
    m,
    l,
    xl,
    level,
    disableEqualOverflow,
}: FlexGridStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();

    const isNestedContainer = container && item;

    const itemPaddingX = item ? getItemPadding('x', level, isNestedContainer, disableEqualOverflow) : undefined;
    const itemPaddingY = item ? getItemPadding('y', level, isNestedContainer, disableEqualOverflow) : undefined;

    const containerMarginX = container ? getContainerMargin('x', level, disableEqualOverflow) : undefined;
    const containerMarginY = container ? getContainerMargin('y', level, disableEqualOverflow) : undefined;

    const root: CSSObject = {
        ...(container
            ? {
                  ...createResponsiveStyles(
                      [
                          {
                              value: mergeResponsiveStyleValues({ columnSpacing, rowSpacing }, theme),
                              getStyles: getContainerResponsiveStyles(theme, level, columns),
                          },
                      ],
                      theme
                  ),
                  display: `flex`,
                  ...(!disableEqualOverflow
                      ? {
                            margin: `${containerMarginY} ${containerMarginX}`,
                        }
                      : {
                            margin: `${containerMarginY} 0 0 ${containerMarginX}`,
                        }),
              }
            : null),
        ...(item
            ? {
                  ...createResponsiveStyles(
                      [
                          {
                              value: { xs, s, m, l, xl },
                              getStyles: getItemResponsiveStyles(level, isNestedContainer),
                          },
                      ],
                      theme
                  ),
                  ...(!disableEqualOverflow
                      ? {
                            padding: `${itemPaddingY} ${itemPaddingX}`,
                        }
                      : {
                            padding: `${itemPaddingY} 0 0 ${itemPaddingX}`,
                        }),
              }
            : null),
        minWidth: 0,
    };

    return {
        styles: {
            root,
        },
    };
}
